import React, { useEffect, useRef, useState } from 'react';
import './MesDaTela.css';

/**
 * O mês da tela: setas, o mês em destaque e uma grade para pular direto.
 *
 * Substituiu o rótulo pequeno em caixa-alta entre duas setas. Ele era o
 * controle que mais muda o que a tela mostra — troca os quatro cards e as
 * seções inteiras — e aparecia como legenda. Agora é o primeiro elemento da
 * página, e um clique no mês abre os doze do ano em vez de obrigar a pessoa
 * a clicar a seta oito vezes para chegar em junho.
 *
 * Quem decide o que "mês atual" significa continua sendo o `App.tsx`; este
 * componente só recebe chaves `AAAA-MM` e devolve chaves `AAAA-MM`.
 */
const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

interface Props {
  mesSelecionado: string;   // AAAA-MM
  mesAtual: string;         // AAAA-MM
  rotulo: string;
  onNavegar: (direcao: 1 | -1) => void;
  onEscolher: (chave: string) => void;
  onHoje: () => void;
}

const MesDaTela: React.FC<Props> = ({ mesSelecionado, mesAtual, rotulo, onNavegar, onEscolher, onHoje }) => {
  const [aberto, setAberto] = useState(false);
  const [anoDaGrade, setAnoDaGrade] = useState(() => Number(mesSelecionado.slice(0, 4)));
  const raiz = useRef<HTMLDivElement>(null);
  const foraDoAtual = mesSelecionado !== mesAtual;

  // A grade abre no ano do mês que está na tela, não no último que ela viu.
  useEffect(() => {
    if (aberto) setAnoDaGrade(Number(mesSelecionado.slice(0, 4)));
  }, [aberto, mesSelecionado]);

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  const escolher = (mes: number) => {
    onEscolher(`${anoDaGrade}-${String(mes + 1).padStart(2, '0')}`);
    setAberto(false);
  };

  return (
    <div className="mes-tela" id="mes-tela" ref={raiz}>
      <div className="mes-tela-barra">
        <button className="mes-tela-seta" onClick={() => onNavegar(-1)} aria-label="Mês anterior" title="Mês anterior">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <button
          className="mes-tela-rotulo"
          onClick={() => setAberto(a => !a)}
          aria-haspopup="dialog"
          aria-expanded={aberto}
          title="Escolher mês"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span>{rotulo}</span>
          <svg className={`mes-tela-caret${aberto ? ' aberto' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        <button className="mes-tela-seta" onClick={() => onNavegar(1)} aria-label="Próximo mês" title="Próximo mês">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>

      {foraDoAtual && (
        <button className="mes-tela-hoje" onClick={onHoje} title="Voltar ao mês atual">
          Voltar para hoje
        </button>
      )}

      {aberto && (
        <div className="mes-tela-grade" role="dialog" aria-label="Escolher mês">
          <div className="mes-tela-ano">
            <button className="mes-tela-seta" onClick={() => setAnoDaGrade(a => a - 1)} aria-label="Ano anterior">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <strong>{anoDaGrade}</strong>
            <button className="mes-tela-seta" onClick={() => setAnoDaGrade(a => a + 1)} aria-label="Próximo ano">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
          <div className="mes-tela-meses">
            {MESES_CURTOS.map((nome, i) => {
              const chave = `${anoDaGrade}-${String(i + 1).padStart(2, '0')}`;
              const classes = [
                'mes-tela-opcao',
                chave === mesSelecionado ? 'selecionado' : '',
                chave === mesAtual ? 'atual' : '',
              ].filter(Boolean).join(' ');
              return (
                <button key={chave} className={classes} onClick={() => escolher(i)} aria-pressed={chave === mesSelecionado}>
                  {nome}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MesDaTela;
